/**
 * The per-section pieces of the public tracking page.
 *
 * Each adapts a ShipmentForTracking into the shared ViewShipments section it
 * wraps — the tracking page shows the same blocks as the signed-in shipment
 * page, but from a tracking id and with no account behind it.
 */

import { ShipmentMap } from "@/Shared/Maps/ShipmentMap";
import ShipmentDetailsSectionForTracking from "@/Shared/Shipment/CustomComponents/ShipmentDetailsSectionForTracking";
import ShipmentNextStepsForDeliverySection from "@/Shared/Shipment/CustomComponents/ShipmentNextStepsForDeliverySection";
import ShipmentNextStepsSection from "@/Shared/Shipment/CustomComponents/ShipmentNextStepsSection";
import ShipmentTopBar from "@/Shared/Shipment/Bars/ShipmentTopBar";
import ShipmentTrackingSection from "@/Shared/Shipment/CustomComponents/ShipmentTrackingSection";
import { shipmentIsDeliveringByAppointment } from "@/Shared/Shipment/Helpers/shipmentIsDeliveringByAppointment";
import { describeDocumentType } from "@/Helpers/describeDocumentType";
import { useOnce } from "@/Hooks/useOnce";
import { useTrackingService } from "@/Services/TrackingService";
import type { Document } from "@freightsimple/generated-dashboard-openapi-client/models/Document";
import type { ShipmentForTracking } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentForTracking";
import type { TrackingInfo } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingInfo";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import moment from "moment";
import { useState } from "react";
import { ShipmentCustomsDocsSectionForTracking } from "@/Shared/Shipment/CustomComponents/ShipmentCustomsDocsSectionForTracking";
import type { DocumentsSectionItem } from "@/Shared/Shipment/CustomComponents/ShipmentDocumentsSectionGeneric";
import ShipmentDocumentsSection from "@/Shared/Shipment/CustomComponents/ShipmentDocumentsSection";

export function mapForShipment(shipment: ShipmentForTracking) {
  return (
    <ShipmentMap
      pickup={shipment.pickupLocation}
      delivery={shipment.deliveryLocation}
      shipmentState={shipment.shipmentState}
    />
  );
}

interface NextStepsSectionForShipmentProps {
  shipment: ShipmentForTracking;
}

export function NextStepsSectionForShipment_(
  props: NextStepsSectionForShipmentProps,
) {
  const { shipment } = props;
  const { downloadPickupPackage } = useTrackingService();

  if (shipment.shipmentState !== ShipmentState.BookingConfirmed) {
    return <></>;
  }

  async function onDownload() {
    await downloadPickupPackage(shipment.trId);
  }

  return <ShipmentNextStepsSection onDownload={onDownload} />;
}

export function NextStepsForDeliverySectionForShipment_(
  props: NextStepsSectionForShipmentProps,
) {
  const { shipment } = props;

  if (shipment.shipmentState !== ShipmentState.InTransit) {
    return <></>;
  }

  const deliveringByAppointment = shipmentIsDeliveringByAppointment(shipment);

  return (
    <ShipmentNextStepsForDeliverySection
      deliveringByAppointment={deliveringByAppointment}
      appointmentSet={shipment.appointmentDate !== undefined}
    />
  );
}

interface DocumentsSectionForShipmentProps {
  shipment: ShipmentForTracking;
}

export function DocumentsSectionForShipment_(
  props: DocumentsSectionForShipmentProps,
) {
  const { shipment } = props;
  const { getDocumentsForTracking, downloadDocument } = useTrackingService();
  const [documentItems, setDocumentItems] = useState<
    Array<DocumentsSectionItem>
  >([]);

  function convert(document: Document): DocumentsSectionItem {
    return {
      onDownloadDocument: () =>
        downloadDocument(
          shipment.trId,
          document.documentId,
          document.documentType,
        ),
      documentDescription: describeDocumentType(document.documentType),
      creationDate: moment(document.createdAt),
      existsYet: true,
      documentType: document.documentType,
    };
  }

  useOnce(async () => {
    const documents = (await getDocumentsForTracking(shipment.trId)).map((d) =>
      convert(d),
    );

    setDocumentItems(documents);
  });

  return <ShipmentDocumentsSection items={documentItems} />;
}

export function topBarForShipment(shipment: ShipmentForTracking) {
  const proNumber = shipment.proNumber;
  const billOfLadingNumber = shipment.billOfLadingNumber || undefined;

  if (!proNumber) {
    return <></>;
  }

  const deliveryAppointment = shipment.deliveryLocation.accessorials.includes(
    "SCHEDULING_APPOINTMENT_REQUIRED",
  );

  return (
    <ShipmentTopBar
      shipmentState={shipment.shipmentState}
      pickupLocation={shipment.pickupLocation}
      deliveryLocation={shipment.deliveryLocation}
      pickupDate={shipment.pickupDate}
      pickupHours={undefined}
      appointmentDate={shipment.appointmentDate}
      expectedDeliveryDate={shipment.expectedDeliveryDate}
      expectedDeliveryHours={shipment.expectedDeliveryHours}
      actualDeliveryDate={shipment.actualDeliveryDate}
      actualDeliveryTime={shipment.actualDeliveryTime}
      predictedLatestDeliveryDate={shipment.latestExpectedDeliveryDate}
      proNumber={proNumber}
      referenceNumber={shipment.deliveryReferenceNumber}
      preferRenderReferenceNumber={true}
      billOfLadingNumber={billOfLadingNumber}
      deliveryAppointment={deliveryAppointment}
    />
  );
}

interface DetailsSectionForShipmentProps {
  shipment: ShipmentForTracking;
  trackingInfo: TrackingInfo | undefined;
}

export function DetailsSectionForShipment_(
  props: DetailsSectionForShipmentProps,
) {
  const { shipment } = props;
  const lineItems = shipment.lineItems;
  const shipmentState = shipment.shipmentState;
  const pickupLocation = shipment.pickupLocation;
  const deliveryLocation = shipment.deliveryLocation;
  const deliveryDeadline = shipment.deliveryDeadline;
  const pickupContact = shipment.pickupContact;
  const deliveryContact = shipment.deliveryContact;
  const pickupDate = moment(shipment.pickupDate).startOf("day");
  const actualDeliveryDate = shipment.actualDeliveryDate;
  const actualDeliveryTime = shipment.actualDeliveryTime;
  const proNumber = shipment.proNumber;
  const expectedDeliveryDate = shipment.expectedDeliveryDate;
  const latestExpectedDeliveryDate = shipment.latestExpectedDeliveryDate;
  const pickupReferenceNumber = shipment.pickupReferenceNumber;
  const deliveryReferenceNumber = shipment.deliveryReferenceNumber;
  const deliveryAmazonReferenceId = shipment.deliveryAmazonReferenceId;
  const deliveryAmazonShipmentId = shipment.deliveryAmazonShipmentId;
  const pickupBoothNumber = shipment.pickupBoothNumber;
  const deliveryBoothNumber = shipment.deliveryBoothNumber;

  const quote = {
    carrierIdentifier: shipment.carrierIdentifier,
    serviceDisplayName: shipment.serviceDisplayName,
  };

  const trackingLines = props.trackingInfo?.trackingLines;
  const mostRecentTrackingLine =
    trackingLines !== undefined
      ? trackingLines[trackingLines.length - 1]
      : undefined;

  return (
    <ShipmentDetailsSectionForTracking
      lineItems={lineItems}
      shipmentState={shipmentState}
      pickupLocation={pickupLocation}
      deliveryLocation={deliveryLocation}
      pickupContact={pickupContact}
      deliveryContact={deliveryContact}
      deliveryDeadline={deliveryDeadline}
      appointmentDate={shipment.appointmentDate}
      pickupDate={pickupDate}
      pickupHours={undefined}
      quote={quote}
      actualDeliveryDate={actualDeliveryDate}
      actualDeliveryTime={actualDeliveryTime}
      proNumber={proNumber}
      expectedDeliveryDate={expectedDeliveryDate}
      latestExpectedDeliveryDate={latestExpectedDeliveryDate}
      pickupReferenceNumber={pickupReferenceNumber}
      deliveryReferenceNumber={deliveryReferenceNumber}
      deliveryAmazonReferenceId={deliveryAmazonReferenceId}
      deliveryAmazonShipmentId={deliveryAmazonShipmentId}
      pickupBoothNumber={pickupBoothNumber}
      deliveryBoothNumber={deliveryBoothNumber}
      notes={undefined}
      broker={undefined}
      mostRecentTrackingLine={mostRecentTrackingLine}
    />
  );
}

interface TrackingSectionForShipmentProps {
  shipment: ShipmentForTracking;
  trackingInfo: TrackingInfo | undefined;
}

export function TrackingSectionForShipment_(
  props: TrackingSectionForShipmentProps,
) {
  const { shipment, trackingInfo } = props;

  if (trackingInfo === undefined) {
    return <></>;
  }

  const lines = trackingInfo.trackingLines.map((line) => {
    return {
      message: line.message,
      time: moment(line.timestamp),
    };
  });

  return (
    <ShipmentTrackingSection
      shipmentState={shipment.shipmentState}
      lines={lines}
    />
  );
}

export function customsDocsSectionForShipment(
  shipment: ShipmentForTracking,
  onUploaded: () => void,
) {
  return (
    <ShipmentCustomsDocsSectionForTracking
      trackingId={shipment.trId}
      onUploaded={onUploaded}
    />
  );
}
